<template>
  <div class="customer-mail-inbox">
    <Tabs v-model="activeTab" display="pill" size="sm" class="customer-mail-inbox__tabs">
      <Tab
        name="needs_reply"
        :title="$t('deals.mail_inbox_needs_reply')"
        :badge="counts.needs_reply || undefined"
      >
        <div class="customer-mail-inbox__list">
          <div v-if="loading" class="customer-mail-inbox__loading">
            <LoadingSpinner />
          </div>
          <EmptyState
            v-else-if="threads.length === 0"
            size="sm"
            :title="$t('deals.mail_inbox_empty')"
            :description="$t('deals.mail_inbox_empty_needs_reply')"
          />
          <div
            v-for="thread in threads"
            :key="thread.id"
            class="customer-mail-inbox__item"
          >
            <div class="customer-mail-inbox__item-main">
              <div class="customer-mail-inbox__item-title">
                {{ thread.subject || $t('deals.mail_no_subject') }}
              </div>
              <div class="customer-mail-inbox__item-meta">
                <span v-if="thread.deal">{{ thread.deal.title }}</span>
                <span v-if="thread.lead">{{ thread.lead.name || thread.lead.email }}</span>
                <span>{{ formatDateTime(thread.last_message_at) }}</span>
              </div>
            </div>
            <Btn size="sm" variant="primary" @click="openReply(thread)">
              {{ $t('deals.mail_reply') }}
            </Btn>
          </div>
        </div>
      </Tab>

      <Tab
        name="unmatched"
        :title="$t('deals.mail_inbox_unmatched')"
        :badge="counts.unmatched || undefined"
      >
        <div class="customer-mail-inbox__list">
          <div v-if="loading" class="customer-mail-inbox__loading">
            <LoadingSpinner />
          </div>
          <EmptyState
            v-else-if="threads.length === 0"
            size="sm"
            :title="$t('deals.mail_inbox_empty')"
            :description="$t('deals.mail_inbox_empty_unmatched')"
          />
          <div
            v-for="thread in threads"
            :key="thread.id"
            class="customer-mail-inbox__item customer-mail-inbox__item--unmatched"
          >
            <div class="customer-mail-inbox__item-main">
              <div class="customer-mail-inbox__item-title">
                {{ thread.subject || $t('deals.mail_no_subject') }}
              </div>
              <div class="customer-mail-inbox__item-meta">
                <span>{{ thread.lead?.email || $t('deals.mail_unknown_sender') }}</span>
                <span>{{ formatDateTime(thread.last_message_at) }}</span>
              </div>
              <TextInput
                :id="`link-deal-${thread.id}`"
                :model-value="dealSearch[thread.id] || ''"
                :placeholder="$t('deals.mail_search_deal')"
                @update:model-value="(value) => handleDealSearch(thread.id, value)"
              />
              <div v-if="dealResults[thread.id]?.length" class="customer-mail-inbox__deals">
                <button
                  v-for="deal in dealResults[thread.id]"
                  :key="deal.id"
                  type="button"
                  class="customer-mail-inbox__deal"
                  @click="linkDeal(thread, deal)"
                >
                  {{ deal.title }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Tab>
    </Tabs>
  </div>
</template>

<script>
  import { ref, watch, onMounted, getCurrentInstance, reactive } from 'vue';
  import { useRouter } from 'vue-router';
  import { dealEmailService } from '@/services/dealEmailService';
  import { dealService } from '@/services/dealService';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';
  import { formatDateTime } from '@/utils/date';
  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import DealEmailReplyModal from '@/components/deals/DealEmailReplyModal.vue';

  export default {
    name: 'CustomerMailInboxModal',

    components: {
      Tabs,
      Tab,
      LoadingSpinner,
      EmptyState,
      TextInput,
      Btn,
    },

    props: {
      modalId: { type: [Number, String], default: null },
      onClose: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const toastStore = useToastStore();
      const uiStore = useUIStore();

      const activeTab = ref('needs_reply');
      const loading = ref(false);
      const threads = ref([]);
      const counts = ref({ unmatched: 0, needs_reply: 0, mine: 0, total: 0 });
      const dealSearch = reactive({});
      const dealResults = reactive({});
      let searchTimers = {};

      const loadCounts = async () => {
        try {
          counts.value = await dealEmailService.getInboxCounts();
        } catch (error) {
          counts.value = { unmatched: 0, needs_reply: 0, mine: 0, total: 0 };
        }
      };

      const loadThreads = async () => {
        loading.value = true;
        try {
          const result = await dealEmailService.getInbox(activeTab.value);
          threads.value = result?.threads || [];
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.mail_inbox_load_failed') });
          threads.value = [];
        } finally {
          loading.value = false;
        }
      };

      const handleDealSearch = (threadId, value) => {
        dealSearch[threadId] = value;
        clearTimeout(searchTimers[threadId]);
        if (!value?.trim()) {
          dealResults[threadId] = [];
          return;
        }
        searchTimers[threadId] = setTimeout(async () => {
          try {
            const result = await dealService.getDeals({ search: value.trim(), limit: 8 });
            dealResults[threadId] = result?.deals || result?.data || [];
          } catch (error) {
            dealResults[threadId] = [];
          }
        }, 300);
      };

      const linkDeal = async (thread, deal) => {
        try {
          await dealEmailService.linkThreadToDeal(thread.id, deal.id);
          toastStore.addToast({ type: 'success', message: $t('deals.mail_linked') });
          await loadCounts();
          await loadThreads();
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.mail_link_failed') });
        }
      };

      const openReply = async (thread) => {
        const dealId = thread.deal_id || thread.deal?.id || null;
        if (dealId) {
          if (typeof props.onClose === 'function') {
            props.onClose();
          } else if (props.modalId != null) {
            uiStore.closeModal(props.modalId);
          }
          await router.push({ name: 'DealDetail', params: { id: dealId } });
        }

        uiStore.addModal(DealEmailReplyModal, {
          title: $t('deals.mail_reply'),
          size: 'md',
          dealId,
          threadId: thread.id,
          initialSubject: thread.subject || '',
          onSent: async () => {
            await loadCounts();
            await loadThreads();
          },
        });
      };

      watch(activeTab, () => loadThreads());

      onMounted(async () => {
        await Promise.all([loadCounts(), loadThreads()]);
      });

      return {
        activeTab,
        loading,
        threads,
        counts,
        dealSearch,
        dealResults,
        formatDateTime,
        handleDealSearch,
        linkDeal,
        openReply,
      };
    },
  };
</script>

<style scoped lang="scss">
  .customer-mail-inbox {
    min-height: 280px;

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding-top: 1rem;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__item {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.75rem 1rem;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      background: var(--slate-50);
    }

    &__item-main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    &__item-title {
      font-weight: 600;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__item-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      color: var(--slate-500);
      @include fontSize('sm');
    }

    &__deals {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__deal {
      text-align: left;
      padding: 0.4rem 0.5rem;
      border: 0;
      border-radius: 6px;
      background: white;
      cursor: pointer;
      @include hover {
        background: var(--brand-50);
      }
    }
  }
</style>
